import { createFileRoute } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, Clock, RefreshCw, XCircle } from "lucide-react";

import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import {
  CANARY_STEP_HINTS,
  CANARY_STEP_KEYS,
  CANARY_STALE_MINUTES,
  canaryStepLabel,
  isCanaryStale,
  type CanaryRun,
} from "@/lib/canary";
import { useCanaryRuns } from "@/lib/use-canary";

export const Route = createFileRoute("/_authenticated/status")({
  head: () => ({
    meta: [
      { title: "Status — MyPriorities" },
      {
        name: "description",
        content:
          "Automated health checks for MyPriorities: sign-in, redirects and the Work hub, checked on a schedule.",
      },
      { property: "og:title", content: "Status — MyPriorities" },
      { property: "og:description", content: "Automated sign-in and Work hub health checks." },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: StatusPage,
});

function timeAgo(iso: string) {
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} hr ago`;
  return `${Math.round(hours / 24)} d ago`;
}

function StepRow({ run, stepKey }: { run: CanaryRun; stepKey: (typeof CANARY_STEP_KEYS)[number] }) {
  const step = run.steps.find((s) => s.key === stepKey);
  const ok = step?.ok ?? false;
  return (
    <div className="flex items-start gap-3 border-t border-border py-3 first:border-t-0">
      {ok ? (
        <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-primary" />
      ) : (
        <XCircle className="mt-0.5 size-4 shrink-0 text-destructive" />
      )}
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium">{canaryStepLabel(stepKey)}</p>
        <p className="text-sm text-muted-foreground">{step?.detail ?? "No result recorded yet."}</p>
        <p className="mt-0.5 text-xs text-muted-foreground/70">{CANARY_STEP_HINTS[stepKey]}</p>
      </div>
      {step ? <span className="shrink-0 text-xs text-muted-foreground">{step.ms} ms</span> : null}
    </div>
  );
}

function StatusPage() {
  const queryClient = useQueryClient();
  const { data: runs = [], isLoading } = useCanaryRuns();
  const latest = runs[0] ?? null;
  const stale = isCanaryStale(latest);
  const healthy = Boolean(latest?.ok) && !stale;

  const passRate = runs.length
    ? Math.round((runs.filter((r) => r.ok).length / runs.length) * 100)
    : null;

  return (
    <AppShell
      title="Status"
      subtitle="Automated checks run against the live site and confirm sign-in, redirects and the Work hub still work after each publish."
      action={
        <Button
          variant="outline"
          onClick={() => {
            void queryClient.invalidateQueries({ queryKey: ["canary-runs"] });
            void queryClient.invalidateQueries({ queryKey: ["canary-latest"] });
          }}
        >
          <RefreshCw className="size-4" /> Refresh
        </Button>
      }
    >
      <div className="space-y-6">
        <section
          className={
            "rounded-xl border p-5 " +
            (healthy ? "border-border bg-card" : "border-destructive/40 bg-destructive/10")
          }
        >
          <div className="flex flex-wrap items-center gap-3">
            {healthy ? (
              <CheckCircle2 className="size-5 text-primary" />
            ) : stale ? (
              <Clock className="size-5 text-destructive" />
            ) : (
              <XCircle className="size-5 text-destructive" />
            )}
            <p className="text-lg font-semibold">
              {isLoading
                ? "Loading checks…"
                : !latest
                  ? "No checks have run yet"
                  : stale
                    ? "Checks have stopped running"
                    : latest.ok
                      ? "Everything is working"
                      : "Something is failing"}
            </p>
            {latest ? (
              <span className="text-sm text-muted-foreground">
                last checked {timeAgo(latest.created_at)} · {latest.duration_ms} ms
              </span>
            ) : null}
          </div>

          {stale && latest ? (
            <p className="mt-2 text-sm text-muted-foreground">
              The last result is more than {CANARY_STALE_MINUTES} minutes old, so the schedule
              itself may have stopped. Treat the result below as out of date.
            </p>
          ) : null}

          {latest ? (
            <p className="mt-2 text-sm text-muted-foreground">
              Checking <span className="font-medium">{latest.target}</span>
              {passRate !== null ? ` · ${passRate}% of the last ${runs.length} runs passed` : ""}
            </p>
          ) : (
            <p className="mt-2 text-sm text-muted-foreground">
              Results appear here within about 15 minutes of a publish.
            </p>
          )}
        </section>

        {latest ? (
          <section className="rounded-xl border border-border bg-card p-5">
            <h2 className="label-caps mb-2">Latest run</h2>
            <div>
              {CANARY_STEP_KEYS.map((key) => (
                <StepRow key={key} run={latest} stepKey={key} />
              ))}
            </div>
          </section>
        ) : null}

        {runs.length > 1 ? (
          <section className="rounded-xl border border-border bg-card p-5">
            <h2 className="label-caps mb-3">History</h2>
            <ul className="space-y-1">
              {runs.map((run) => (
                <li
                  key={run.id}
                  className="flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-border py-2 text-sm first:border-t-0"
                >
                  {run.ok ? (
                    <CheckCircle2 className="size-4 shrink-0 text-primary" />
                  ) : (
                    <XCircle className="size-4 shrink-0 text-destructive" />
                  )}
                  <span className="font-medium">{run.ok ? "Passed" : "Failed"}</span>
                  <span className="text-muted-foreground">
                    {new Date(run.created_at).toLocaleString()}
                  </span>
                  {run.failed_steps.length ? (
                    <span className="text-muted-foreground">
                      {run.failed_steps.map(canaryStepLabel).join(" · ")}
                    </span>
                  ) : null}
                  <span className="ml-auto text-xs text-muted-foreground">
                    {run.duration_ms} ms
                  </span>
                </li>
              ))}
            </ul>
          </section>
        ) : null}
      </div>
    </AppShell>
  );
}
